import { useQuery } from "react-query";
import memberActivityAPI from "./member_activity";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const useGetList = (schoolId, accessToken, page, pageSize, status) => {
  return useQuery(
    ["trainings", schoolId, accessToken, page, pageSize, status],
    async () => {
      if (!schoolId) {
        return null;
      }
      if (status === undefined) {
        status = "";
      }
      try {
        const response = await fetch(
          BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings?page=${page}&pageSize=${pageSize}&status=${status}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch trainings!");
        }
        if (response.status >= 299) {
          throw new Error("Erreur de récupération de la liste des formations!");
        }
        const result = await response.json();
        return result;
      } catch (error) {
        // Handle the error
        console.error("Error fetching trainings:", error);
        throw error;
      }
    }
  );
};

const GetOne = (schoolId, trainingId, accessToken) => {
  return useQuery(["training", schoolId, trainingId, accessToken], async () => {
    if (!schoolId || !trainingId) {
      return null;
    }
    try {
      const response = await fetch(
        BACKEND_URL + `/api/v1/schools/${schoolId}/trainings/${trainingId}`,
        {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + accessToken,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch training!");
      }
      if (response.status >= 299) {
        throw new Error("Erreur de récupération de la formation!");
      }
      const result = await response.json();
      return result;
    } catch (error) {
      throw error;
    }
  });
};

const GetOneTraining = async (schoolId, trainingId, memberId = null) => {
  try {
    const response = await fetch(
      BACKEND_URL + `/api/v1/schools/${schoolId}/trainings/${trainingId}`,
      {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error("Erreur de récupération de la formation!");
    }
    const result = await response.json();
    
    // Update member activity asynchronously if memberId is provided
    // This is done in the background to avoid blocking the main request
    if (memberId && schoolId) {
      memberActivityAPI.updateMemberActivity(
        schoolId,
        memberId,
        "" // Empty access token since we're using credentials: "include"
      ).catch(error => {
        // Silently handle errors to avoid disrupting the main functionality
        console.warn('Failed to update member activity:', error);
      });
    }
    
    return result;
  } catch (error) {
    throw error;
  }
};


const GetTrainingPreferences = async (
  schoolId,
  trainingId,
  accessToken
) => {
  try {

    const response = await fetch(
      BACKEND_URL +
      `/api/v1/schools/${schoolId}/trainings/${trainingId}/preferences`,
      {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch training preferences!");
    }
    if (response.status >= 299) {
      throw new Error("Erreur de récupération de préférences de la formation!");
    }
    const trainingObj = await response.json();
    return trainingObj?.data;

  } catch (error) {
    console.error(
      `There was an error in fetching the training preferences: ${error.message}`
    );
    return null;
  }
};


const CreateOne = async (schoolId, dummyTrainingId, accessToken, data) => {
  try {
    const response = await fetch(
      BACKEND_URL + "/api/v1/schools/" + schoolId + "/trainings",
      {
        method: "POST",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de création de la formation!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateOne = async (
  schoolId,
  trainingId,
  accessToken,
  dummyTrainingId,
  data
) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      dummyTrainingId,
      {
        method: "PATCH",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification de la formation!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateTrainingAvatar = async (
  schoolId,
  trainingId,
  accessToken,
  newAvatar
) => {
  try {
    let formData = new FormData();
    formData.append("file", newAvatar);
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/avatar",
      {
        method: "POST",
        body: formData,
        credentials: "include",
        headers: {
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification de la salle!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateTrainingPreferences = async (
  schoolId,
  trainingId,
  accessToken,
  data
) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId + "/preferences",
      {
        method: "PATCH",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification des préférences de la formation!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateTrainingStatus = async (
  schoolId,
  trainingId,
  accessToken,
  data
) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId + "/update_status",
      {
        method: "PATCH",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification du status de la formation!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const DeleteOne = async (schoolId, dummyTrainingId, accessToken, trainingId) => {
  try {
    const response = await fetch(
      BACKEND_URL + "/api/v1/schools/" + schoolId + "/trainings/" + trainingId,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de suppression de la formation!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};


const GetPaymentSettings = async (schoolId, trainingId, accessToken) => {
  try {
    const response = await fetch(
      BACKEND_URL + `/api/v1/schools/${schoolId}/trainings/${trainingId}/payment-settings`,
      {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Failed to fetch payment settings!");
    }
    return await response.json();
  } catch (err) {
    throw err;
  }
};

const UpdatePaymentSettings = async (schoolId, trainingId, accessToken, data) => {
  try {
    const response = await fetch(
      BACKEND_URL + `/api/v1/schools/${schoolId}/trainings/${trainingId}/payment-settings`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Failed to update payment settings!");
    }
    return await response.json();
  } catch (err) {
    throw err;
  }
};

const CheckHelloAssoCompliance = async (schoolId, trainingId, accessToken) => {
  try {
    const response = await fetch(
      BACKEND_URL + `/api/v1/schools/${schoolId}/trainings/${trainingId}/payment-settings/helloasso/check-compliance`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Failed to check HelloAsso compliance!");
    }
    return await response.json();
  } catch (err) {
    throw err;
  }
};

const trainingsAPI = {
  useGetList,
  GetOne,
  GetOneTraining,
  GetTrainingPreferences,
  CreateOne,
  UpdateOne,
  UpdateTrainingAvatar,
  UpdateTrainingPreferences,
  UpdateTrainingStatus,
  DeleteOne,
  GetPaymentSettings,
  UpdatePaymentSettings,
  CheckHelloAssoCompliance,
};

export default trainingsAPI;
